import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Eye, EyeOff, Lock, Check } from "lucide-react";
import { Link } from "react-router-dom";
import countries from "../../../json/CountryFlagCode.json";
import api from "../../../lib/api.ts";
import { useLanguage } from "../../../context/LanguageContext";

const SellerRegistrationForm = ({
  onSubmit,
  isLoading,
  accountTypes,
  accountType,
  setAccountType,
  initialValues = {},
  isUniquenessValidated = false,
  onProceed,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState("AE");
  const [uniquenessErrors, setUniquenessErrors] = useState({
    email: "",
    mobileNo: "",
  });
  const [isCheckingUniqueness, setIsCheckingUniqueness] = useState({
    email: false,
    mobileNo: false,
  });

  const { language, translate } = useLanguage();
  const isRTL = language === "ar";

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      name: initialValues?.name || "",
      email: initialValues?.email || "",
      mobileNo: initialValues?.mobileNo || "",
      // password: initialValues.password || "",
    },
  });

  // Update form values when initialValues change
  useEffect(() => {
    if (initialValues && Object.keys(initialValues).length > 0) {
      reset({
        name: initialValues?.name || "",
        email: initialValues?.email || "",
        mobileNo: initialValues?.mobileNo || "",
        // password: initialValues.password || "",
      });
    }
  }, [initialValues, reset]);

  // Function to check uniqueness
  const checkUniqueness = async (field, value) => {
    if (!value || value.trim() === "") {
      setUniquenessErrors((prev) => ({ ...prev, [field]: "" }));
      return;
    }

    setIsCheckingUniqueness((prev) => ({ ...prev, [field]: true }));

    try {
      const payload = { [field]: value };
      await api.post(`/auth/checkunique`, payload);

      // If successful, clear any existing error
      setUniquenessErrors((prev) => ({ ...prev, [field]: "" }));
    } catch (error) {
      console.error(`Error checking ${field} uniqueness:`, error);

      if (error.response && error.response.data) {
        const errorData = error.response.data;
        if (errorData.message) {
          setUniquenessErrors((prev) => ({
            ...prev,
            [field]: errorData.message,
          }));
        } else {
          setUniquenessErrors((prev) => ({
            ...prev,
            [field]:
              field === "email"
                ? translate("auth.signup.emailExists")
                : translate("auth.signup.phoneExists"),
          }));
        }
      } else {
        setUniquenessErrors((prev) => ({
          ...prev,
          [field]:
            field === "email"
              ? translate("auth.signup.emailExists")
              : translate("auth.signup.phoneExists"),
        }));
      }
    } finally {
      setIsCheckingUniqueness((prev) => ({ ...prev, [field]: false }));
    }
  };

  // Handle onBlur for email
  const handleEmailBlur = (e) => {
    const email = e.target.value;
    if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      checkUniqueness("email", email);
    }
  };

  // Handle onBlur for mobile number
  const handleMobileBlur = (e) => {
    const mobileNo = e.target.value;
    if (mobileNo && mobileNo.trim() !== "") {
      checkUniqueness("mobileNo", mobileNo);
    }
  };

  return (
    <form
      dir={isRTL ? "rtl" : "ltr"}
      lang={language}
      onSubmit={handleSubmit((data) => {
        const country = countries.find((c) => c.code === selectedCountry);
        const dial = country?.dial_code || "";
        const raw = (data.mobileNo || "").replace(/\s+/g, "");
        const full = `${dial}${raw}`;
        onSubmit({ ...data, mobileNo: full, selectedCountry });
      })}
      className='w-full'
    >
      <div className='space-y-2 sm:space-y-3'>
        <h3 className='text-lg sm:text-2xl capitalize md:text-[28px] font-bold mb-1 text-[#131313] text-center mt-6 sm:mt-8'>
          {translate("auth.signup.createSellerAccount")}
        </h3>
        <p className='text-[#13131399] capitalize pb-4 sm:pb-8 md:pb-10 text-xs sm:text-sm md:text-[16px] text-center'>
          {translate("auth.signup.sellerSubtitle")}
        </p>
      </div>

      {/* Account Type Selection */}
      <div className='mb-3 sm:mb-4'>
        <div className='grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-2'>
          {accountTypes.map((type) => (
            <label key={type.id} className='cursor-pointer'>
              <input
                type='radio'
                name='accountType'
                value={type.id}
                checked={accountType === type.id}
                onChange={() => setAccountType(type.name)}
                className='hidden'
              />
              <div
                className={`flex items-center p-2.5 sm:p-3 rounded-lg transition-all duration-200 border-2 ${
                  accountType === type.name
                    ? "bg-orange-50 border-orange-500"
                    : "bg-white border-orange-300"
                }`}
              >
                <div
                  className={`w-4 h-4 sm:w-5 sm:h-5 rounded-full border-2 flex items-center justify-center ${
                    isRTL ? "ml-2 sm:ml-3" : "mr-2 sm:mr-3"
                  } flex-shrink-0 ${
                    accountType === type.name
                      ? "border-orange-500 bg-orange-500"
                      : "border-gray-300"
                  }`}
                >
                  {accountType === type.name && (
                    <Check className='w-2.5 h-2.5 sm:w-3 sm:h-3 text-white' />
                  )}
                </div>
                <span className='font-medium text-gray-700 text-xs sm:text-sm md:text-base'>
                  {type.name === "Individual"
                    ? translate("auth.signup.individual")
                    : type.name === "Corporate"
                    ? translate("auth.signup.corporate")
                    : type.name}
                </span>
              </div>
            </label>
          ))}
        </div>
        <hr className='my-2 border-gray-300' />
      </div>

      {/* Name */}
      <div className='mb-3 sm:mb-4'>
        <label className='block text-xs sm:text-sm md:text-[16px] font-medium text-[#131313] mb-1.5 sm:mb-2'>
          {translate("auth.signup.fullName")}
        </label>
        <input
          type='text'
          placeholder={translate("auth.signup.fullNamePlaceholder")}
          className={`w-full ${
            isRTL ? "pr-3 sm:pr-4 pl-3 sm:pl-4" : "pl-3 sm:pl-4 pr-3 sm:pr-4"
          } py-2.5 sm:py-3 text-[13px] sm:text-base border ${
            errors.name ? "border-red-500" : "border-[#13131399]"
          } rounded-lg bg-white focus:outline-none`}
          {...register("name", {
            required: translate("auth.signup.nameRequired"),
            minLength: {
              value: 2,
              message: translate("auth.signup.nameMinLength"),
            },
          })}
        />
        {errors.name && (
          <p className='mt-1 text-[12px] sm:text-sm text-red-500'>
            {errors.name.message}
          </p>
        )}
      </div>

      {/* Phone Number */}
      <div className='mb-3 sm:mb-4'>
        <label className='block text-xs sm:text-sm md:text-[16px] font-medium text-[#131313] mb-1.5 sm:mb-2'>
          {translate("auth.signup.phoneNumber")}
        </label>
        <div className='flex'>
          <div
            className={`flex w-full h-11 sm:h-12 border border-[#13131399] rounded-lg bg-white overflow-hidden focus-within:ring-orange-500 ${
              isRTL ? "flex-row-reverse" : ""
            }`}
          >
            {/* Country Code Select with Flag */}
            {/* <div className="relative">
              <div className="absolute left-2 top-1/2 transform -translate-y-1/2 w-10 h-5">
                <img
                  src={`https://flagcdn.com/w40/${selectedCountry.toLowerCase()}.png`}
                  alt="flag"
                  className="w-full h-full object-cover rounded-sm"
                />
              </div>
              <select
                value={selectedCountry}
                onChange={(e) => setSelectedCountry(e.target.value)}
                className="h-full pl-12 bg-white focus:outline-none appearance-none cursor-pointer rounded-l-lg"
              >
                {countries.map((country) => (
                  <>
                    <img src={country.flag} alt="" />
                    <option key={country.code} value={country.code}>
                      {country.dial_code}
                    </option>
                  </>
                ))}
              </select>
            </div> */}

            <div className='relative'>
              <div
                className={`absolute ${
                  isRTL ? "right-1.5 sm:right-2" : "left-1.5 sm:left-2"
                } top-1/2 transform -translate-y-1/2 w-8 h-4 sm:w-10 sm:h-5`}
              >
                {/* Show flag matching the selected country code */}
                <img
                  src={`https://flagcdn.com/w40/${selectedCountry.toLowerCase()}.png`}
                  alt='flag'
                  className='w-full h-full object-cover rounded-sm'
                />
              </div>
              <select
                value={selectedCountry}
                onChange={(e) => setSelectedCountry(e.target.value)}
                className={`h-full ${
                  isRTL
                    ? "pr-9 sm:pr-12 rounded-r-lg"
                    : "pl-9 sm:pl-12 rounded-l-lg"
                } text-xs sm:text-sm bg-white focus:outline-none appearance-none cursor-pointer`}
              >
                {countries
                  .sort((a, b) => a.dial_code.localeCompare(b.dial_code))
                  .map((country) => (
                    <option key={country.code} value={country.code}>
                      {country.dial_code}
                    </option>
                  ))}
              </select>
            </div>
            {/* ------------------------------ */}

            {/* Phone Number Input */}
            <input
              type='tel'
              placeholder={translate("auth.signup.phonePlaceholder")}
              maxLength='15'
              className={`flex-1 px-3 sm:px-4 h-full bg-white focus:outline-none text-[13px] sm:text-base ${
                errors.mobileNo || uniquenessErrors.mobileNo
                  ? "border-red-500"
                  : ""
              }`}
              {...register("mobileNo", {
                required: translate("auth.signup.phoneRequired"),
                pattern: {
                  value: /^[0-9\s]+$/,
                  message: translate("auth.signup.phoneInvalid"),
                },
              })}
              onBlur={handleMobileBlur}
            />
          </div>
        </div>
        {errors.mobileNo && (
          <p className='mt-1 text-[12px] sm:text-sm text-red-500'>
            {errors.mobileNo.message}
          </p>
        )}
        {uniquenessErrors.mobileNo && (
          <p className='mt-1 text-[12px] sm:text-sm text-red-500'>
            {uniquenessErrors.mobileNo}
          </p>
        )}
      </div>

      {/* Email */}
      <div className='mb-3 sm:mb-4'>
        <label className='block text-xs sm:text-sm md:text-[16px] font-medium text-[#131313] mb-1.5 sm:mb-2'>
          {translate("auth.signup.email")}
        </label>
        <div className='relative'>
          <div
            className={`absolute ${
              isRTL ? "right-2 sm:right-3" : "left-2 sm:left-3"
            } top-2.5 sm:top-3 scale-90 sm:scale-100`}
          >
            <svg
              width='24'
              height='24'
              viewBox='0 0 24 24'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                fill-rule='evenodd'
                clip-rule='evenodd'
                d='M12 1.25C9.37665 1.25 7.25 3.37665 7.25 6C7.25 8.62335 9.37665 10.75 12 10.75C14.6234 10.75 16.75 8.62335 16.75 6C16.75 3.37665 14.6234 1.25 12 1.25ZM8.75 6C8.75 4.20507 10.2051 2.75 12 2.75C13.7949 2.75 15.25 4.20507 15.25 6C15.25 7.79493 13.7949 9.25 12 9.25C10.2051 9.25 8.75 7.79493 8.75 6Z'
                fill='#888888'
              />
              <path
                fill-rule='evenodd'
                clip-rule='evenodd'
                d='M12 12.25C9.96067 12.25 8.07752 12.7208 6.67815 13.5204C5.3 14.3079 4.25 15.5101 4.25 17C4.25 18.4899 5.3 19.6921 6.67815 20.4796C8.07752 21.2792 9.96067 21.75 12 21.75C14.0393 21.75 15.9225 21.2792 17.3219 20.4796C18.7 19.6921 19.75 18.4899 19.75 17C19.75 15.5101 18.7 14.3079 17.3219 13.5204C15.9225 12.7208 14.0393 12.25 12 12.25ZM5.75 17C5.75 16.2807 6.26701 15.483 7.42236 14.8228C8.55649 14.1747 10.1733 13.75 12 13.75C13.8267 13.75 15.4435 14.1747 16.5776 14.8228C17.733 15.483 18.25 16.2807 18.25 17C18.25 17.7193 17.733 18.517 16.5776 19.1772C15.4435 19.8253 13.8267 20.25 12 20.25C10.1733 20.25 8.55649 19.8253 7.42236 19.1772C6.26701 18.517 5.75 17.7193 5.75 17Z'
                fill='#888888'
              />
            </svg>
          </div>
          <input
            type='text'
            placeholder={translate("auth.signup.emailPlaceholder")}
            className={`w-full ${
              isRTL
                ? "pr-9 sm:pr-10 pl-3 sm:pl-4"
                : "pl-9 sm:pl-10 pr-3 sm:pr-4"
            } py-2.5 sm:py-3 text-[13px] sm:text-base border ${
              errors.email || uniquenessErrors.email
                ? "border-red-500"
                : "border-[#13131399]"
            } rounded-lg bg-white focus:outline-none`}
            {...register("email", {
              required: translate("auth.signup.emailRequired"),
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: translate("auth.signup.emailInvalid"),
              },
            })}
            onBlur={handleEmailBlur}
          />
        </div>
        {errors.email && (
          <p className='mt-1 text-sm text-red-500'>{errors.email.message}</p>
        )}
        {uniquenessErrors.email && (
          <p className='mt-1 text-sm text-red-500'>{uniquenessErrors.email}</p>
        )}
      </div>

      {/* Password */}
      <div className='mb-4'>
        <label className='block text-sm md:text-[16px] font-medium text-[#131313] mb-2'>
          {translate("auth.signup.password")}
        </label>
        <div className='relative'>
          <div className={`absolute ${isRTL ? "right-2" : "left-2"} top-3`}>
            <svg
              width='24'
              height='24'
              viewBox='0 0 24 24'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M8 17C8.55229 17 9 16.5523 9 16C9 15.4477 8.55229 15 8 15C7.44772 15 7 15.4477 7 16C7 16.5523 7.44772 17 8 17Z'
                fill='#888888'
              />
              <path
                d='M12 17C12.5523 17 13 16.5523 13 16C13 15.4477 12.5523 15 12 15C11.4477 15 11 15.4477 11 16C11 16.5523 11.4477 17 12 17Z'
                fill='#888888'
              />
              <path
                d='M17 16C17 16.5523 16.5523 17 16 17C15.4477 17 15 16.5523 15 16C15 15.4477 15.4477 15 16 15C16.5523 15 17 15.4477 17 16Z'
                fill='#888888'
              />
              <path
                fill-rule='evenodd'
                clip-rule='evenodd'
                d='M6.75 8C6.75 5.10051 9.10051 2.75 12 2.75C14.4453 2.75 16.5018 4.42242 17.0846 6.68694C17.1879 7.08808 17.5968 7.32957 17.9979 7.22633C18.3991 7.12308 18.6405 6.7142 18.5373 6.31306C17.788 3.4019 15.1463 1.25 12 1.25C8.27208 1.25 5.25 4.27208 5.25 8V9.30277C5.02317 9.31872 4.80938 9.33948 4.60825 9.36652C3.70814 9.48754 2.95027 9.74643 2.34835 10.3483C1.74643 10.9503 1.48754 11.7081 1.36652 12.6082C1.24996 13.4752 1.24998 14.5775 1.25 15.9451V16.0549C1.24998 17.4225 1.24996 18.5248 1.36652 19.3918C1.48754 20.2919 1.74643 21.0497 2.34835 21.6516C2.95027 22.2536 3.70814 22.5125 4.60825 22.6335C5.47522 22.75 6.57754 22.75 7.94513 22.75H16.0549C17.4225 22.75 18.5248 22.75 19.3918 22.6335C20.2919 22.5125 21.0497 22.2536 21.6517 21.6516C22.2536 21.0497 22.5125 20.2919 22.6335 19.3918C22.75 18.5248 22.75 17.4225 22.75 16.0549V15.9451C22.75 14.5775 22.75 13.4752 22.6335 12.6082C22.5125 11.7081 22.2536 10.9503 21.6517 10.3483C21.0497 9.74643 20.2919 9.48754 19.3918 9.36652C18.5248 9.24996 17.4225 9.24998 16.0549 9.25H7.94513C7.52161 9.24999 7.12353 9.24999 6.75 9.25344V8ZM4.80812 10.8531C4.07435 10.9518 3.68577 11.1322 3.40901 11.409C3.13225 11.6858 2.9518 12.0743 2.85315 12.8081C2.75159 13.5635 2.75 14.5646 2.75 16C2.75 17.4354 2.75159 18.4365 2.85315 19.1919C2.9518 19.9257 3.13225 20.3142 3.40901 20.591C3.68577 20.8678 4.07435 21.0482 4.80812 21.1469C5.56347 21.2484 6.56459 21.25 8 21.25H16C17.4354 21.25 18.4365 21.2484 19.1919 21.1469C19.9257 21.0482 20.3142 20.8678 20.591 20.591C20.8678 20.3142 21.0482 19.9257 21.1469 19.1919C21.2484 18.4365 21.25 17.4354 21.25 16C21.25 14.5646 21.2484 13.5635 21.1469 12.8081C21.0482 12.0743 20.8678 11.6858 20.591 11.409C20.3142 11.1322 19.9257 10.9518 19.1919 10.8531C18.4365 10.7516 17.4354 10.75 16 10.75H8C6.56459 10.75 5.56347 10.7516 4.80812 10.8531Z'
                fill='#888888'
              />
            </svg>
          </div>
          <input
            type={showPassword ? "text" : "password"}
            placeholder={translate("auth.signup.passwordPlaceholder")}
            className={`w-full ${
              isRTL ? "pr-10 pl-12" : "pl-10 pr-12"
            } py-3 border ${
              errors.password ? "border-red-500" : "border-[#13131399]"
            } rounded-lg bg-white focus:outline-none`}
            {...register("password", {
              required: translate("auth.signup.passwordRequired"),
              minLength: {
                value: 6,
                message: translate("auth.signup.passwordMinLength"),
              },
              pattern: {
                value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                message: translate("auth.signup.passwordPattern"),
              },
            })}
          />
          <button
            type='button'
            onClick={() => setShowPassword(!showPassword)}
            className={`absolute ${
              isRTL ? "left-4" : "right-4"
            } top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600`}
          >
            {showPassword ? (
              <Eye className='w-4 h-4' />
            ) : (
              <svg
                width='24'
                height='24'
                viewBox='0 0 24 24'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  fill-rule='evenodd'
                  clip-rule='evenodd'
                  d='M22.2954 6.31059C22.6761 6.47376 22.8525 6.91467 22.6893 7.29539L22 6.99995C22.6893 7.29539 22.6894 7.29521 22.6893 7.29539L22.6886 7.29707L22.6875 7.29955L22.6843 7.30691L22.6737 7.33099C22.6647 7.35112 22.6519 7.37934 22.6353 7.41502C22.602 7.48637 22.5533 7.58769 22.4889 7.7141C22.36 7.96675 22.1676 8.32063 21.9085 8.73641C21.4829 9.41945 20.8724 10.2776 20.062 11.1301L21.0303 12.0985C21.3232 12.3913 21.3232 12.8662 21.0303 13.1591C20.7374 13.452 20.2625 13.452 19.9697 13.1591L18.9691 12.1585C18.3094 12.7112 17.5529 13.23 16.6951 13.6562L17.6286 15.0909C17.8545 15.4381 17.7562 15.9027 17.409 16.1286C17.0618 16.3545 16.5973 16.2562 16.3713 15.909L15.2822 14.2351C14.5028 14.4896 13.659 14.6626 12.75 14.7246V16.4999C12.75 16.9142 12.4142 17.2499 12 17.2499C11.5858 17.2499 11.25 16.9142 11.25 16.4999V14.7246C10.369 14.6645 9.54916 14.5002 8.78989 14.2584L7.71581 15.9091C7.48991 16.2562 7.02532 16.3546 6.67813 16.1287C6.33095 15.9027 6.23263 15.4382 6.45853 15.091L7.37095 13.6887C6.50657 13.2666 5.74387 12.7501 5.07848 12.1982L4.1175 13.1592C3.82461 13.4521 3.34974 13.4521 3.05684 13.1592C2.76395 12.8663 2.76395 12.3914 3.05684 12.0985L3.98061 11.1748C3.15605 10.3151 2.53531 9.4465 2.10283 8.75462C1.8399 8.33398 1.64466 7.97559 1.51394 7.71964C1.44854 7.59157 1.39916 7.48889 1.36543 7.41658C1.34856 7.38042 1.33559 7.35183 1.32648 7.33143L1.31568 7.30704L1.31244 7.29959L1.31135 7.29708L1.31095 7.29613C1.31087 7.29595 1.31063 7.29539 1.99998 6.99995L1.31095 7.29613C1.14778 6.91541 1.32382 6.47376 1.70455 6.31059C2.08495 6.14756 2.52545 6.3235 2.68894 6.70357C2.68888 6.70344 2.689 6.7037 2.68894 6.70357L2.6899 6.70576L2.69597 6.71947C2.70186 6.73267 2.71146 6.75386 2.72479 6.78243C2.75145 6.83959 2.79302 6.9262 2.84982 7.03742C2.96351 7.26004 3.13768 7.58022 3.37479 7.95955C3.85039 8.72042 4.57163 9.70704 5.55567 10.6215C6.42157 11.4263 7.48265 12.1675 8.75171 12.6558C9.7062 13.023 10.7854 13.2499 12 13.2499C13.2417 13.2499 14.3421 13.0127 15.3125 12.6308C16.5739 12.1343 17.6277 11.3882 18.4867 10.5819C19.4562 9.67191 20.1669 8.69511 20.6354 7.94315C20.869 7.56825 21.0406 7.25221 21.1526 7.03262C21.2086 6.9229 21.2495 6.83752 21.2758 6.78119C21.2889 6.75304 21.2983 6.73217 21.3041 6.71919L21.3101 6.70571L21.3106 6.70451C21.3106 6.70461 21.3107 6.70441 21.3106 6.70451M22.2954 6.31059C21.9148 6.14746 21.4739 6.32399 21.3106 6.70451L22.2954 6.31059ZM2.68894 6.70357C2.68888 6.70344 2.689 6.7037 2.68894 6.70357V6.70357Z'
                  fill='#888888'
                />
              </svg>
            )}
          </button>
        </div>
        {errors.password && (
          <p className='mt-1 text-sm text-red-500'>{errors.password.message}</p>
        )}
      </div>

      {/* Submit button */}
      {!isUniquenessValidated ? (
        <button
          type='submit'
          disabled={isLoading}
          className={`w-full border border-[#F15A29] bg-white text-[#F15A29] py-3 rounded-lg hover:bg-[#F15A29] hover:text-white transition-all duration-200 font-medium mb-4 ${
            isLoading ? "opacity-90 cursor-not-allowed" : ""
          }`}
        >
          {isLoading
            ? translate("auth.signup.processing")
            : translate("auth.signup.createSellerAccountButton")}
        </button>
      ) : (
        <button
          type='button'
          onClick={onProceed}
          className='w-full border border-[#F15A29] bg-[#F15A29] text-white py-3 rounded-lg hover:bg-[#F15A29] hover:text-white transition-all duration-200 font-medium mb-4'
        >
          {translate("auth.signup.proceedToCompleteProfile")}
        </button>
      )}

      {/* Switch to login */}
      <p className='text-sm text-center text-gray-600'>
        {translate("auth.signup.alreadyHaveAccount")}{" "}
        <Link
          to='/login'
          state={{ selectedTab: "Seller" }}
          className='text-orange-600 hover:text-orange-700 hover:underline font-medium'
        >
          {translate("auth.signup.signIn")}
        </Link>
      </p>
    </form>
  );
};

export default SellerRegistrationForm;
